import { usePagination, scrollToElement, clampTotalPages, getDictionaryEntry } from '@greystar/lib';
import { LayoutContainer, PaginationBar } from '@greystar/designsystem';
import { useEffect } from 'react';
import { useI18n } from 'next-localization';

interface PaginationButton {
  value: number;
  label: string;
  isDisabled?: boolean;
}

interface PaginationProps {
  readonly totalResults: number;
  readonly isFilterApplied: boolean;
  readonly onPaginationChange: () => void; // Added callback for pagination change
}

export default function Pagination({
  totalResults,
  isFilterApplied,
  onPaginationChange,
}: PaginationProps) {
  const { state, methods } = usePagination();
  const { t } = useI18n();

  // Calculate totalCount: use totalResults if <= 39, else approximate totalEntries
  const totalCount = totalResults <= 39 ? totalResults : state.totalPages * state.pageSize;

  // Calculate total pages based on filter state
  const totalPages = clampTotalPages(
    isFilterApplied && totalCount <= 39 ? Math.ceil(totalCount / state.pageSize) : state.totalPages
  );

  // Reset to page 0 if current page exceeds total pages when filters are applied
  useEffect(() => {
    if (isFilterApplied && state.page >= totalPages && totalPages > 0) {
      methods?.selectPage(0);
      scrollToElement('search--listing');
      onPaginationChange(); // Call on pagination reset
    }
  }, [isFilterApplied, totalPages, state.page, methods, onPaginationChange]);

  // Show pagination only if totalPages > 1
  if (totalPages <= 1 && !isFilterApplied) {
    return null;
  }

  const getPaginationButtons = (): PaginationButton[] => {
    const buttons: PaginationButton[] = [];
    const currentPage = state.page + 1; // Convert to 1-based index

    // Always show first page
    buttons.push({
      value: 1,
      label: '1',
    });

    if (totalPages <= 5) {
      addRangeButtons(buttons, 2, totalPages);
      return buttons;
    }

    if (currentPage <= 4) {
      addRangeButtons(buttons, 2, 5);
      addEllipsis(buttons);
    } else if (currentPage === 5) {
      addEllipsis(buttons);
      addRangeButtons(buttons, 4, 6);
      addEllipsis(buttons);
    } else if (currentPage >= totalPages - 2) {
      addEllipsis(buttons);
      addRangeButtons(buttons, totalPages - 4, totalPages);
    } else {
      addEllipsis(buttons);
      addRangeButtons(buttons, currentPage - 1, currentPage + 1);
      addEllipsis(buttons);
    }

    // Always show last page if not already shown
    if (currentPage < totalPages - 2) {
      buttons.push({
        value: totalPages,
        label: String(totalPages),
      });
    }

    return buttons;
  };

  const addRangeButtons = (buttons: PaginationButton[], start: number, end: number): void => {
    for (let i = start; i <= end; i++) {
      buttons.push({
        value: i,
        label: String(i),
      });
    }
  };

  const addEllipsis = (buttons: PaginationButton[]): void => {
    buttons.push({
      value: -1,
      label: '...',
      isDisabled: true,
    });
  };

  const ariaLabels = {
    prevButton: getDictionaryEntry(t('pagination-previous-button-label'), 'Go to previous page'),
    nextButton: getDictionaryEntry(t('pagination-next-button-label'), 'Go to next page'),
    paginationBar: getDictionaryEntry(t('pagination-bar-label'), 'Pagination navigation'),
    paginationButton: getDictionaryEntry(t('pagination-button-label'), 'Page'),
  };

  return (
    <LayoutContainer
      paddingBottom="xl"
      paddingTop="2xl"
      flexDirection="row"
      margin="auto"
      justifyContent="center"
    >
      <PaginationBar
        prevButton={{
          onClick: () => {
            scrollToElement('search--listing');
            methods?.previousPage();
            onPaginationChange(); // Call on prev button click
          },
          isDisabled: methods === undefined || state.page === 0,
        }}
        nextButton={{
          onClick: () => {
            scrollToElement('search--listing');
            methods?.nextPage();
            onPaginationChange(); // Call on next button click
          },
          isDisabled: methods === undefined || state.page === totalPages - 1,
        }}
        paginationButtons={getPaginationButtons()}
        onPaginationButtonClick={(page: number) => {
          if (page > 0) {
            // Skip ellipsis clicks
            scrollToElement('search--listing');
            methods?.selectPage(page - 1); // Convert back to 0-based index
            onPaginationChange(); // Call on page button click
          }
        }}
        selectedPagination={state.page + 1}
        paginationText={`Page ${state.page + 1} of ${totalPages}`}
        ariaLabels={ariaLabels}
      />
    </LayoutContainer>
  );
}
